import React, { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faTimes, faSpinner, faUniversity, faHashtag,
    faCalendar, faMoneyBill, faBuilding, faFileAlt,
    faDownload, faBalanceScale,
} from '@fortawesome/free-solid-svg-icons';
import bankReconService from '../../services/bankReconService';

const BankStatementDetailModal = ({ statement, onClose }) => {
    const history = useHistory();
    const [lines, setLines] = useState([]);
    const [loading, setLoading] = useState(true);
    const [downloading, setDownloading] = useState(false);

    useEffect(() => {
        if (!statement) return;
        setLoading(true);
        bankReconService.getStatementLines(statement.id)
            .then(res => setLines(res.data || []))
            .catch(err => console.error('Failed to load lines:', err))
            .finally(() => setLoading(false));
    }, [statement]);

    if (!statement) return null;

    const handleDownload = async () => {
        setDownloading(true);
        try {
            const response = await bankReconService.downloadStatement(statement.id);
            const blob = new Blob([response.data]);
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = statement.original_filename || 'bank_statement';
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (err) {
            console.error('Download failed:', err);
        } finally {
            setDownloading(false);
        }
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return '-';
        try {
            return new Date(dateStr).toLocaleDateString('en-GB', {
                day: '2-digit', month: 'short', year: 'numeric',
            });
        } catch {
            return dateStr;
        }
    };

    const formatAmount = (val) => {
        const num = parseFloat(val);
        if (isNaN(num)) return '-';
        return num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    };

    const totalDebits = lines.reduce((sum, l) => {
        if (l.credit_debit === 'DEBIT') return sum + Math.abs(parseFloat(l.amount) || 0);
        return sum;
    }, 0);

    const totalCredits = lines.reduce((sum, l) => {
        if (l.credit_debit === 'CREDIT') return sum + Math.abs(parseFloat(l.amount) || 0);
        return sum;
    }, 0);

    const netMovement = totalCredits - totalDebits;

    return (
        <div className="md-modal-overlay" onClick={onClose}>
            <div className="bank-detail-modal" onClick={(e) => e.stopPropagation()}>
                <div className="md-modal-header">
                    <div className="md-modal-header-text">
                        <FontAwesomeIcon icon={faFileAlt} className="md-modal-header-icon" />
                        <h2>Statement Details</h2>
                    </div>
                    <button className="md-modal-close" onClick={onClose}>
                        <FontAwesomeIcon icon={faTimes} />
                    </button>
                </div>

                <div className="md-modal-body">
                    <div className="bank-detail-header-grid">
                        {statement.bank_name && (
                            <div className="bank-detail-field">
                                <FontAwesomeIcon icon={faUniversity} className="bank-detail-field-icon" />
                                <div>
                                    <span className="bank-detail-label">Bank</span>
                                    <span className="bank-detail-value">{statement.bank_name}</span>
                                </div>
                            </div>
                        )}
                        {statement.iban && (
                            <div className="bank-detail-field">
                                <FontAwesomeIcon icon={faHashtag} className="bank-detail-field-icon" />
                                <div>
                                    <span className="bank-detail-label">IBAN</span>
                                    <span className="bank-detail-value bank-recon-iban">{statement.iban}</span>
                                </div>
                            </div>
                        )}
                        {statement.hms_entity_name && (
                            <div className="bank-detail-field">
                                <FontAwesomeIcon icon={faBuilding} className="bank-detail-field-icon" />
                                <div>
                                    <span className="bank-detail-label">Entity</span>
                                    <span className="bank-detail-value">{statement.hms_entity_name}</span>
                                </div>
                            </div>
                        )}
                        {statement.statement_date && (
                            <div className="bank-detail-field">
                                <FontAwesomeIcon icon={faCalendar} className="bank-detail-field-icon" />
                                <div>
                                    <span className="bank-detail-label">Statement Date</span>
                                    <span className="bank-detail-value">{formatDate(statement.statement_date)}</span>
                                </div>
                            </div>
                        )}
                        {statement.currency && (
                            <div className="bank-detail-field">
                                <FontAwesomeIcon icon={faMoneyBill} className="bank-detail-field-icon" />
                                <div>
                                    <span className="bank-detail-label">Currency</span>
                                    <span className="bank-detail-value">{statement.currency}</span>
                                </div>
                            </div>
                        )}
                    </div>

                    {statement.docuware_document_id && (
                        <div className="bank-detail-download-card">
                            <div className="bank-detail-download-info">
                                <FontAwesomeIcon icon={faFileAlt} className="bank-detail-download-file-icon" />
                                <div>
                                    <span className="bank-detail-download-filename">{statement.original_filename}</span>
                                    <span className="bank-detail-download-hint">Original uploaded file</span>
                                </div>
                            </div>
                            <button
                                className="bank-detail-download-btn"
                                onClick={handleDownload}
                                disabled={downloading}
                            >
                                <FontAwesomeIcon icon={downloading ? faSpinner : faDownload} spin={downloading} />
                                {downloading ? 'Downloading...' : 'Download'}
                            </button>
                        </div>
                    )}

                    {loading ? (
                        <div className="bank-detail-loading">
                            <FontAwesomeIcon icon={faSpinner} spin /> Loading transaction lines...
                        </div>
                    ) : lines.length === 0 ? (
                        <div className="bank-detail-loading">No transaction lines found.</div>
                    ) : (
                        <>
                            <div className="bank-detail-lines-header">
                                Transaction Lines ({lines.length})
                            </div>
                            <div className="bank-detail-table-wrap">
                                <table className="invoice-list-table bank-detail-lines-table">
                                    {/* Explicit widths are load-bearing. With
                                        table-layout:fixed the browser uses these
                                        instead of measuring content, which is what
                                        lets the long columns truncate at all --
                                        overflow/text-overflow on a <td> is ignored
                                        while the table is auto-layout, so the text
                                        used to paint straight over its neighbours. */}
                                    <colgroup>
                                        <col style={{ width: '36px' }} />
                                        <col style={{ width: '92px' }} />
                                        <col />
                                        <col style={{ width: '150px' }} />
                                        <col style={{ width: '150px' }} />
                                        <col style={{ width: '90px' }} />
                                        <col style={{ width: '90px' }} />
                                        <col style={{ width: '100px' }} />
                                        {/* 112px, not 96: the "Unmatched" badge plus cell padding measures 109px,
                                            and a badge that overflows its own column is the same
                                            defect this table was fixed for. */}
                                        <col style={{ width: '112px' }} />
                                    </colgroup>
                                    <thead>
                                        <tr>
                                            <th>#</th>
                                            <th>Date</th>
                                            <th>Description</th>
                                            <th>Reference</th>
                                            <th>Partner</th>
                                            <th>Debit</th>
                                            <th>Credit</th>
                                            <th>Balance</th>
                                            <th>Status</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {lines.map((line) => {
                                            const isCredit = line.credit_debit === 'CREDIT';
                                            return (
                                                <tr key={line.id}>
                                                    <td>{line.line_number}</td>
                                                    <td>{formatDate(line.transaction_date)}</td>
                                                    {/* The truncation lives on this inner
                                                        span, not the cell: a table-cell
                                                        cannot clip its own content. `title`
                                                        keeps the full value reachable. */}
                                                    <td>
                                                        <span className="cell-text" title={line.description || ''}>
                                                            {line.description || '-'}
                                                        </span>
                                                    </td>
                                                    <td>
                                                        <span className="cell-text" title={line.reference || ''}>
                                                            {line.reference || '-'}
                                                        </span>
                                                    </td>
                                                    <td>
                                                        <span className="cell-text" title={line.partner_name || ''}>
                                                            {line.partner_name || '-'}
                                                        </span>
                                                    </td>
                                                    <td className="bank-detail-amount-cell">
                                                        {!isCredit ? formatAmount(line.amount) : ''}
                                                    </td>
                                                    <td className="bank-detail-amount-cell bank-detail-credit">
                                                        {isCredit ? formatAmount(line.amount) : ''}
                                                    </td>
                                                    <td className="bank-detail-amount-cell">
                                                        {line.running_balance != null ? formatAmount(line.running_balance) : ''}
                                                    </td>
                                                    <td>
                                                        <span className={`status-badge ${line.reconciliation_status === 'matched' || line.reconciliation_status === 'manually_matched' ? 'active' : 'inactive'}`}>
                                                            {line.reconciliation_status === 'matched' ? 'Matched' :
                                                             line.reconciliation_status === 'manually_matched' ? 'Manual' : 'Unmatched'}
                                                        </span>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>

                            <div className="bank-detail-summary">
                                <div className="bank-detail-summary-item">
                                    <span>Total Debits:</span>
                                    <span className="bank-detail-debit">{formatAmount(totalDebits)}</span>
                                </div>
                                <div className="bank-detail-summary-item">
                                    <span>Total Credits:</span>
                                    <span className="bank-detail-credit">{formatAmount(totalCredits)}</span>
                                </div>
                                <div className="bank-detail-summary-item bank-detail-summary-net">
                                    <span>Net Movement:</span>
                                    <span className={netMovement >= 0 ? 'bank-detail-credit' : 'bank-detail-debit'}>
                                        {netMovement >= 0 ? '+' : ''}{formatAmount(netMovement)}
                                    </span>
                                </div>
                            </div>
                        </>
                    )}
                </div>

                <div className="md-modal-footer">
                    <button className="md-btn-cancel" onClick={onClose}>Close</button>
                    <button
                        className="bank-detail-download-btn"
                        onClick={() => { onClose(); history.push(`/invoicing/bank-reconciliation/reconcile/${statement.id}`); }}
                    >
                        <FontAwesomeIcon icon={faBalanceScale} /> Reconcile
                    </button>
                </div>
            </div>
        </div>
    );
};

export default BankStatementDetailModal;
